import { createOpenAI } from '@ai-sdk/openai';
import { type LanguageModelV1 } from 'ai';
import { type TersaModel, type TersaProvider, providers } from '../../providers';
import { withModelIds } from '@flowmuse/models/shared/with-model-ids';

export type PriceBracket = 'lowest' | 'low' | 'high' | 'highest';

type TersaTextModel = TersaModel & {
    providers: (TersaProvider & {
        model: LanguageModelV1;
        getCost: ({ input, output }: { input: number; output: number }) => number;
    })[];
};

// 统一网关
const OPENROUTER_BASE_URL = 'https://openrouter.ai/api/v1';
const openrouter = createOpenAI({
    baseURL: OPENROUTER_BASE_URL,
    apiKey: process.env.OPENROUTER_API_KEY,
});

const openRouterProvider = (provider: TersaProvider, modelId: string, cost: number) => ({
    ...provider,
    model: openrouter(modelId),
    getCost: () => cost,
});

export const textModels: Record<string, TersaTextModel> = withModelIds({
    'gpt-4o': {
        default: true,
        chef: providers.openai,
        providers: [openRouterProvider(providers.openai, 'openai/gpt-4o', 0.02)],
        priceIndicator: 'lowest',
        price: '2 credits',
    },
    'gpt-5.6-luna': {
        chef: providers.openai,
        providers: [openRouterProvider(providers.openai, 'openai/gpt-5.6-luna', 0.05)],
        priceIndicator: 'lowest',
        price: '5 credits',
    },
    'gemini-3.7-flash': {
        chef: providers.google,
        providers: [openRouterProvider(providers.google, 'google/gemini-3.7-flash', 0.02)],
        priceIndicator: 'lowest',
        price: '2 credits',
    },
    'gemini-3.1-pro-preview': {
        chef: providers.google,
        providers: [openRouterProvider(providers.google, 'google/gemini-3.1-pro-preview', 0.05)],
        priceIndicator: 'lowest',
        price: '5 credits',
    },
    'claude-5.0-sonnet': {
        chef: providers.anthropic,
        providers: [openRouterProvider(providers.anthropic, 'anthropic/claude-sonnet-5', 0.02)],
        priceIndicator: 'lowest',
        price: '2 credits',
    },
    'claude-opus-5': {
        chef: providers.anthropic,
        providers: [openRouterProvider(providers.anthropic, 'anthropic/claude-opus-5', 0.05)],
        priceIndicator: 'lowest',
        price: '5 credits',
    },
    'claude-fable-5': {
        chef: providers.anthropic,
        providers: [openRouterProvider(providers.anthropic, 'anthropic/claude-fable-5', 0.1)],
        priceIndicator: 'lowest',
        price: '10 credits',
    },
    'grok-4.6': {
        chef: providers.xai,
        providers: [openRouterProvider(providers.xai, 'x-ai/grok-4.6', 0.02)],
        priceIndicator: 'lowest',
        price: '2 credits',
    },
    'deepseek-v4-flash': {
        chef: providers.deepseek,
        providers: [openRouterProvider(providers.deepseek, 'deepseek/deepseek-v4-flash', 0.02)],
        priceIndicator: 'lowest',
        price: '2 credits',
    },
    'deepseek-v4-pro': {
        chef: providers.deepseek,
        providers: [openRouterProvider(providers.deepseek, 'deepseek/deepseek-v4-pro', 0.05)],
        priceIndicator: 'lowest',
        price: '5 credits',
    },
    'qwen-3.7-flash': {
        chef: providers.alibaba,
        providers: [openRouterProvider(providers.alibaba, 'qwen/qwen3.7-flash', 0.02)],
        priceIndicator: 'lowest',
        price: '2 credits',
    },
    'qwen-3.7-plus': {
        chef: providers.alibaba,
        providers: [openRouterProvider(providers.alibaba, 'qwen/qwen3.7-plus', 0.02)],
        priceIndicator: 'lowest',
        price: '2 credits',
    },
    'qwen-3.8-max': {
        chef: providers.alibaba,
        providers: [openRouterProvider(providers.alibaba, 'qwen/qwen3.8-max', 0.05)],
        priceIndicator: 'lowest',
        price: '5 credits',
    },
    'glm-5.3': {
        chef: providers.zhipu,
        providers: [openRouterProvider(providers.zhipu, 'z-ai/glm-5.3', 0.02)],
        priceIndicator: 'lowest',
        price: '2 credits',
    },
    'glm-5.3-flash': {
        chef: providers.zhipu,
        providers: [openRouterProvider(providers.zhipu, 'z-ai/glm-5.3-flash', 0.02)],
        priceIndicator: 'lowest',
        price: '2 credits',
    },
    'seed-2-1-turbo': {
        chef: providers.bytedance,
        providers: [openRouterProvider(providers.bytedance, 'bytedance-seed/seed-2-1-turbo', 0.02)],
        priceIndicator: 'lowest',
        price: '2 credits',
    },
    'kimi-k3': {
        chef: providers.moonshot,
        providers: [openRouterProvider(providers.moonshot, 'moonshotai/kimi-k3', 0.05)],
        priceIndicator: 'lowest',
        price: '5 credits',
    },
    'minimax-m3': {
        chef: providers.minimax,
        providers: [openRouterProvider(providers.minimax, 'minimax/minimax-m3', 0.02)],
        priceIndicator: 'lowest',
        price: '2 credits',
    },
    'mimo-v2.5': {
        chef: providers.xiaomi,
        providers: [openRouterProvider(providers.xiaomi, 'xiaomi/mimo-v2.5', 0.02)],
        priceIndicator: 'lowest',
        price: '2 credits',
    },
    'mimo-v2.5-pro': {
        chef: providers.xiaomi,
        providers: [openRouterProvider(providers.xiaomi, 'xiaomi/mimo-v2.5-pro', 0.05)],
        priceIndicator: 'lowest',
        price: '5 credits',
    },
});